import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import { shortLabel } from "./FrsTab";

const room = (it) => [it.building, it.floor && `Fl ${it.floor}`, it.room_number].filter(Boolean).join(" / ");

/** Diff any two versions (Original / Current / vN). Grouped by change type, keyed by item identity. */
export default function FrsDiff({ project, overview }) {
  const [a, setA] = useState(overview.original_version_id ? "original" : "current");
  const [b, setB] = useState("current");
  const [d, setD] = useState(null);

  useEffect(() => {
    setD(null);
    opsApi.frsDiff(project.id, a, b).then(setD).catch(() => toast.error("Could not compute the diff"));
  }, [project.id, a, b]);

  const opts = [["current", "Current"], ...(overview.original_version_id ? [["original", "Original"]] : []),
    ...overview.versions.filter((v) => v.kind !== "original").map((v) => [v.id, `v${v.version_no} · ${v.source}`])];
  const Pick = ({ v, set, id }) => (
    <select value={v} onChange={(e) => set(e.target.value)} data-testid={id} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs">
      {opts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
  );

  return (
    <div className="space-y-3" data-testid="ops-frs-diff">
      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">Compare <Pick v={a} set={setA} id="ops-frs-diff-a" /> with <Pick v={b} set={setB} id="ops-frs-diff-b" /></div>
      {!d ? <div className="flex items-center gap-2 py-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Comparing…</div> : (
        <>
          <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="ops-frs-diff-summary">
            <span className="font-medium">{d.counts.added} added · {d.counts.removed} removed · {d.counts.changed} changed · {d.counts.unchanged} unchanged.</span> {d.summary}
          </p>
          {[["added", "Added", d.added, "text-emerald-700"], ["removed", "Removed", d.removed, "text-rose-700"]].map(([k, label, list, tone]) => list.length > 0 && (
            <div key={k} className="rounded-lg border border-slate-200 bg-white">
              <div className={`border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide ${tone}`}>{label} ({list.length})</div>
              {list.map((it) => (
                <div key={it.frs_item_key} className="flex items-center gap-3 border-b border-slate-50 px-3 py-1.5 text-sm" data-testid={`ops-frs-diff-${k}-${it.frs_item_key}`}>
                  <span className="w-40 text-slate-500">{room(it)}</span><span className="w-16 text-xs text-slate-500">{it.scope_code}</span>
                  <span className="text-slate-800">{shortLabel(it)}</span><span className="ml-auto tabular-nums">{it.effective_quantity ?? it.quantity}</span>
                </div>
              ))}
            </div>
          ))}
          {d.changed.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-amber-700">Changed ({d.changed.length})</div>
              {d.changed.map((c) => (
                <div key={c.frs_item_key} className="border-b border-slate-50 px-3 py-1.5 text-sm" data-testid={`ops-frs-diff-changed-${c.frs_item_key}`}>
                  <div className="flex items-center gap-3"><span className="w-40 text-slate-500">{room(c.after)}</span><span className="w-16 text-xs text-slate-500">{c.after.scope_code}</span><span className="text-slate-800">{shortLabel(c.after)}</span></div>
                  <div className="mt-0.5 flex flex-wrap gap-3 pl-40 text-xs text-slate-600">
                    {Object.entries(c.fields).map(([f, ch]) => <span key={f}><span className="text-slate-400">{f}:</span> <span className="line-through">{String(ch.previous || "—")}</span> → <span className="font-medium">{String(ch.new || "—")}</span></span>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
